import { api } from '../client';

/**
 * The exception band — everything outstanding across the platform, ranked.
 *
 * Six modules feed one list. They are gathered on the server rather than here
 * because they have to be ranked *against each other*: a lapsed certificate
 * blocking a job and a rental a month overdue are not comparable until
 * something decides which the reader should see first.
 */

export type ExceptionKind =
  | 'JOB_BLOCKED'
  | 'JOB_NOT_ASSESSED'
  | 'CERT_BLOCKING'
  | 'ASSET_OVERDUE'
  | 'RENTAL_OVERDUE'
  | 'RENTAL_NO_DUE_DATE'
  | 'WORK_ORDER_OVERDUE'
  | 'SHIPMENT_DELAYED'
  | 'PERMIT_BREACHED'
  | 'CONSIGNMENT_BELOW_MIN'
  | 'CONSIGNMENT_NO_AGREED_LEVEL';

/**
 * BREACHED — a limit has already been passed and the exposure is live.
 * ACT_NOW  — late, and every day it stays late costs something.
 * PLAN     — not late yet, or nobody has established whether it is.
 */
export type Severity = 'BREACHED' | 'ACT_NOW' | 'PLAN';

export interface ExceptionRow {
  kind: ExceptionKind;
  severity: Severity;
  title: string;
  detail: string;
  /** Every row opens a record. A row that cannot be acted on is not a worklist item. */
  href: string;
  /** Null where no date was ever set — which is not nought days late. */
  daysOverdue: number | null;
}

export interface Exceptions {
  /** The one-line answer to "is everything all right?". */
  statement: string;
  counts: Record<Severity, number>;
  /** How many rows of each kind, so a KPI can quote one without refiltering. */
  byKind: Partial<Record<ExceptionKind, number>>;
  total: number;
  rows: ExceptionRow[];
}

export const dashboardApi = {
  async getExceptions(): Promise<Exceptions> {
    const response = await api.get<Exceptions>('/dashboard/exceptions');
    return response.data;
  },
};
